import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Users, Leaf, Building, Heart, Shield } from 'lucide-react';

const AchievementsSection = () => {
  const achievements = [
    {
      category: "Économie",
      icon: <TrendingUp className="w-6 h-6" />,
      color: "text-primary",
      bgColor: "bg-primary/10",
      items: [
        "Lancement du projet PROMOREN pour mobiliser 46,6 millions de m³ d'eau",
        "Progression des exportations agricoles : mangues (+12%) et tomates (+18%)",
        "Remboursement des dettes aux agriculteurs et subventions à la campagne agricole",
        "Début de la production pétrolière - Le Sénégal devient producteur d'hydrocarbures"
      ]
    },
    {
      category: "Gouvernance",
      icon: <Shield className="w-6 h-6" />,
      color: "text-secondary",
      bgColor: "bg-secondary/10",
      items: [
        "Refondation des institutions et renforcement de la transparence",
        "Annulation de nominations controverses au Conseil supérieur de la magistrature",
        "Rationalisation des nominations gouvernementales",
        "Dissolution d'institutions jugées coûteuses et non essentielles"
      ]
    },
    {
      category: "Agriculture",
      icon: <Leaf className="w-6 h-6" />,
      color: "text-accent",
      bgColor: "bg-accent/10",
      items: [
        "Construction d'un magasin de stockage de 1000 tonnes à Potou",
        "Aménagement de 12 000 hectares de terres agricoles via PROMOREN",
        "Soutien aux petits producteurs locaux",
        "Renforcement de la sécurité alimentaire nationale"
      ]
    },
    {
      category: "Développement",
      icon: <Building className="w-6 h-6" />,
      color: "text-primary",
      bgColor: "bg-primary/10",
      items: [
        "Lancement de la Vision Sénégal 2050",
        "Présentation du New Deal Technologique",
        "Renforcement de l'intégration africaine et de la CEDEAO",
        "Amélioration des relations diplomatiques internationales"
      ]
    },
    {
      category: "Social",
      icon: <Heart className="w-6 h-6" />,
      color: "text-secondary",
      bgColor: "bg-secondary/10",
      items: [
        "Amélioration de l'accès aux services essentiels",
        "Promotion de l'équité sociale et de l'inclusion",
        "Renforcement du système éducatif",
        "Développement des infrastructures de santé"
      ]
    },
    {
      category: "Environnement",
      icon: <Users className="w-6 h-6" />,
      color: "text-accent",
      bgColor: "bg-accent/10",
      items: [
        "Assainissement de la pêche et protection du littoral",
        "Arrêt de l'occupation anarchique du littoral",
        "Gestion durable des ressources naturelles",
        "Promotion de la transition écologique"
      ]
    }
  ];

  const stats = [
    { number: "46,6M", label: "m³ d'eau mobilisés", description: "Projet PROMOREN" },
    { number: "12K", label: "hectares aménagés", description: "Terres agricoles" },
    { number: "+18%", label: "exportations tomates", description: "Croissance agricole" },
    { number: "2050", label: "Vision Sénégal", description: "Agenda de transformation" }
  ];

  return (
    <section id="realisations" className="py-20 bg-gradient-card">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* En-tête de section */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Réalisations
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Découvrez les accomplissements majeurs du Président Bassirou Diomaye Faye 
              depuis son investiture en avril 2024.
            </p>
          </div>

          {/* Statistiques clés */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {stats.map((stat, index) => (
              <Card key={index} className="text-center shadow-card hover:shadow-elegant transition-all duration-300">
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-primary mb-2">{stat.number}</div>
                  <div className="text-sm font-semibold text-foreground mb-1">{stat.label}</div>
                  <div className="text-xs text-muted-foreground">{stat.description}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Réalisations par catégorie */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {achievements.map((category, index) => (
              <Card key={index} className="shadow-card hover:shadow-elegant transition-all duration-300 transform hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`p-3 rounded-lg ${category.bgColor}`}>
                      <div className={category.color}>
                        {category.icon}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-primary">
                        {category.category}
                      </h3>
                      <Badge variant="outline" className="text-xs mt-1">
                        {category.items.length} réalisations
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {category.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Citation inspirante */}
          <div className="mt-16">
            <Card className="shadow-elegant bg-gradient-primary text-white">
              <CardContent className="p-8 text-center">
                <blockquote className="text-xl md:text-2xl font-light italic mb-4">
                  "Je m'engage à me consacrer pleinement dans la refondation de nos institutions 
                  et le renforcement des fondements de notre vivre ensemble."
                </blockquote>
                <cite className="text-lg font-semibold">
                  — Président Bassirou Diomaye Faye
                </cite>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AchievementsSection;